import { Link } from 'react-router-dom'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader } from '../ui/card'
import { Badge } from '../ui/badge'
import {
  Download as DownloadIcon,
  Calendar,
  ArrowDownToLine,
  FileDown,
  Tag,
  Github,
  Info,
  Check,
  ExternalLink,
  Smartphone
} from 'lucide-react'

export default function Download() {
  // Static app information
  const appInfo = {
    name: 'TextRedge Gateway',
    version: 'v2.6.2',
    releaseDate: '2026-06-09T00:00:00.000Z',
    downloadUrl: '/downloads/textredge.apk',
    fileSize: 6357569, // Size in bytes
    releaseNotes: 'Bulk SMS chunk processing with pause/resume support, high-priority single SMS preemption, and personalized per-recipient messages. Re-released with a proper production signing certificate (Resource Edge Studios).'
  }

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B'
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
    else return (bytes / 1048576).toFixed(1) + ' MB'
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="container mx-auto max-w-4xl">
        {/* Navigation */}
        <div className="mb-6">
          <Link to="/" className="text-blue-600 hover:text-blue-800 mr-4">Home</Link>
          <Link to="/dashboard" className="text-blue-600 hover:text-blue-800 mr-4">Dashboard</Link>
          <Link to="/support" className="text-blue-600 hover:text-blue-800">Support</Link>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center rounded-full border px-3 py-1 text-sm bg-blue-50 border-blue-200 text-blue-700 mb-4">
            <DownloadIcon className="h-3.5 w-3.5 mr-2" /> Download TextRedge
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 mb-4">
            Download TextRedge App
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Transform your Android device into a powerful SMS gateway with our
            easy-to-use application.
          </p>
        </div>

        {/* Latest Release Card */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-green-100 text-green-800">
                    Latest Version
                  </Badge>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {appInfo.name}
                </h2>
              </div>

              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white" asChild>
                <a
                  href={appInfo.downloadUrl}
                  download="textredge.apk"
                >
                  <ArrowDownToLine className="mr-2 h-5 w-5" />
                  Download Now
                </a>
              </Button>
            </div>
          </CardHeader>

          <CardContent>
            <div className="flex flex-wrap gap-4 mb-6 text-sm text-gray-600">
              <div className="flex items-center">
                <Tag className="h-4 w-4 mr-1" />
                <span>Version: {appInfo.version}</span>
              </div>
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                <span>Released: {formatDate(appInfo.releaseDate)}</span>
              </div>
              <div className="flex items-center">
                <FileDown className="h-4 w-4 mr-1" />
                <span>Size: {formatFileSize(appInfo.fileSize)}</span>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Release Notes</h3>
              <div className="prose max-w-none text-gray-600">
                <p>{appInfo.releaseNotes}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Features Section */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <Card>
            <CardHeader>
              <div className="flex items-center">
                <Smartphone className="h-6 w-6 mr-2 text-blue-600" />
                <h3 className="text-xl font-semibold">Easy Setup</h3>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Download, install, and configure your Android device as an SMS gateway 
                in just a few minutes.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center">
                <Check className="h-6 w-6 mr-2 text-green-600" />
                <h3 className="text-xl font-semibold">Reliable</h3>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Built for reliability with automatic failover, message queuing, 
                and delivery confirmations.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Installation Instructions */}
        <Card>
          <CardHeader>
            <div className="flex items-center">
              <Info className="h-6 w-6 mr-2 text-blue-600" />
              <h3 className="text-xl font-semibold">Installation Instructions</h3>
            </div>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal list-inside space-y-2 text-gray-600">
              <li>Download the APK file using the button above</li>
              <li>Enable "Install from Unknown Sources" in your Android settings</li>
              <li>Install the downloaded APK file</li>
              <li>Open the TextRedge app and follow the setup wizard</li>
              <li>Connect your device to your TextRedge dashboard</li>
            </ol>
            <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> This app requires SMS permissions to function properly. 
                Make sure to grant all requested permissions during installation.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Support Link */}
        <div className="text-center mt-12">
          <Button variant="outline" asChild>
            <Link
              to="/support"
              className="inline-flex items-center"
            >
              <Info className="mr-2 h-4 w-4" />
              Need Help? Visit Support
            </Link>
          </Button>
        </div>
      </div>
    </div>
  )
}